/**
 * useIntegrationSyncWorkflow
 *
 * Config-driven composable for any integration that maps an external data
 * source to Routine tasks. Covers Gmail, Todoist, Asana, and future additions.
 *
 * Each integration supplies an IntegrationSyncConfig describing only what varies:
 *   - integration key, label, page description
 *   - two filter option cards + how to fetch sub-picker items
 *   - optional per-connection label resolver (e.g. Gmail email address)
 *   - how to build enableDataCollection parameters and display names
 *   - how to generate ready-state hint strings
 *   - which database column names map to url / scheduled / completed
 *   - the Vue component used for the success illustration + caption
 *
 * Phase structure:
 *   mainPhase: "empty" | "ready" — whether any accounts are connected
 *   wizardPhase: "connecting" | "configure" | "mapping" | "creating" | "failed"
 *                | "success"
 *                | null
 *   wizardConnId: which connection the wizard is currently configuring
 *
 * Connecting an account (addAccount) and setting up task sync (openTaskSyncWizard)
 * are decoupled: OAuth lands on the ready screen without forcing a configure step.
 * Each connected account can have several independent task syncs (one enabled
 * data collection each, distinguished by their parameters — e.g. one for a
 * specific Asana project and one for all tasks), each with its own name, hint,
 * completion action and remove action. Disconnecting an account removes all of
 * its task syncs.
 */

import type { MembraneClient } from "@membranehq/sdk";
import type { Component, Ref } from "vue";
import { computed, ref, watch } from "vue";
import type { RouteLocationNormalizedLoaded, Router } from "vue-router";

import type {
  Cell,
  Controller,
  GraphDatabaseDictionaryDictionary,
  IntegrationAppEnabledDataCollection,
  IntegrationAppEnabledDataCollectionId,
  TaskBindingDictionary,
} from "@ctrl/controller-rpc";

import type { SelectOption } from "@/components/base/BaseInputSelect.vue";
import type { StatusValueOption } from "@/components/settings/status-value-mapping";
import { logger as rendererLogger } from "@/logger/renderer";
import { isRecord, optionalStringProperty } from "@/utils/unknown";

import {
  ensureDataCollectionBinding,
  resolveStatusCellMapping,
  sameDataCollectionParameters,
  watchForDataCollectionReady,
  type DataCollectionFailure,
  type FieldNames,
  type StatusCellMapping,
} from "./useDataCollectionBinding";
import type {
  EnabledDataCollectionWithWorkspace,
  MembraneSetup,
} from "./useMembraneSetup";

const logger = rendererLogger.renderer.wrapperForModule(
  "useIntegrationSyncWorkflow"
);

// ── Public types ───────────────────────────────────────────────────────────

export type FilterOption = {
  /** Emoji shown on the option card. */
  icon: string;
  /** Label shown on the option card. */
  label: string;
};

export type FilterItem = { id: string; name: string };

export type TaskSyncMappingProperty = {
  id: string;
  name: string;
  kind: "select" | "status" | "checkbox" | "date";
  options: Array<{ id: string; name: string; group?: string }>;
};

export type TaskSyncMapping = {
  statusPropertyId: string;
  datePropertyId: string;
  statusCells?: StatusCellMapping;
};

/**
 * The values a status property can hold, labelled the way the provider shows
 * them. Checkboxes have no options of their own, so they get the two booleans
 * the connector writes.
 */
export function providerStatusOptions(
  propertyValue: TaskSyncMappingProperty | undefined
): StatusValueOption[] {
  if (!propertyValue) return [];
  if (propertyValue.kind === "checkbox") {
    return [
      { value: "true", label: "Checked" },
      { value: "false", label: "Unchecked" },
    ];
  }
  return propertyValue.options.map((option) => ({
    value: option.id,
    label: option.name,
  }));
}

export type IllustrationRow = {
  name: string;
  project: string;
  completed?: boolean;
  highlight?: boolean;
};

export type IntegrationSyncConfig = {
  /** Membrane integration key, e.g. "gmail", "todoist", "asana". */
  integrationKey: string;
  /** Human-readable label used in UI strings and the db name prefix. */
  label: string;
  /** Description shown below the integration header. */
  description: string;
  /** Data collection key to enable and watch. */
  collectionKey: string;
  /** First option card in the configure step (no sub-picker). */
  filterOptionA: FilterOption;
  /**
   * Second option card in the configure step (has a sub-picker). Omit when the
   * integration only supports a single sync scope (e.g. Attio's public
   * connector lists all tasks and cannot filter by assignee).
   */
  filterOptionB?: FilterOption;
  /**
   * Optional third option card, also with a sub-picker. Only Jira uses one so
   * far: its issues can be scoped to a project or to a saved filter, and the
   * two are different enough that collapsing them into one picker would put
   * projects and filters in the same list.
   */
  filterOptionC?: FilterOption;
  /**
   * When true, the collection cannot be enabled without a filter selection
   * (e.g. Microsoft To Do has no "all tasks" endpoint — tasks live under a
   * specific list, so `taskListId` is mandatory). The wizard then skips the
   * "all vs. specific" option cards and shows a single required picker sourced
   * from `fetchFilterItems`, and submit stays disabled until an item is chosen.
   */
  requiresFilter?: boolean;
  /** Fetches items for option B's sub-picker (labels, projects, …). */
  fetchFilterItems: (
    connId: string,
    membrane: MembraneClient
  ) => Promise<FilterItem[]>;
  /**
   * Optional sub-picker for option A. When present, option A also requires a
   * selection (like option B) instead of being a pick-free default.
   */
  fetchFilterItemsA?: (
    connId: string,
    membrane: MembraneClient
  ) => Promise<FilterItem[]>;
  /** Fetches items for option C's sub-picker. Required when filterOptionC is set. */
  fetchFilterItemsC?: (
    connId: string,
    membrane: MembraneClient
  ) => Promise<FilterItem[]>;
  /** Optional: resolve a human-readable label for a connection (e.g. Gmail address). */
  fetchConnectionLabel?: (
    connId: string,
    membrane: MembraneClient
  ) => Promise<string | undefined>;
  /** Returns the enableDataCollection parameters for the chosen filter. */
  getParameters: (
    choice: "optionA" | "optionB" | "optionC",
    filterId: string,
    mapping?: TaskSyncMapping
  ) => Record<string, string>;
  /** Returns the database display name for the enabled data collection. */
  getDisplayName: (
    choice: "optionA" | "optionB" | "optionC",
    filterName: string,
    connectionLabel?: string
  ) => string;
  /** Returns the ready-state hint text for a data collection entry. */
  getHint: (
    parameters: Record<string, unknown>,
    filterItems: FilterItem[],
    storedName: string | null
  ) => string;
  /**
   * Returns the short display name of a configured task sync, shown in the
   * per-account sync list (e.g. "All tasks", "Design tasks", "Starred emails").
   */
  getSyncName: (
    parameters: Record<string, unknown>,
    filterItems: FilterItem[]
  ) => string;
  /**
   * Optional: reverse-map stored parameters back to the choice + filterId that
   * produced them. Used by "How to use" to show the correct illustration state
   * for an already-configured sync. If absent, the illustration falls back to
   * optionA defaults.
   */
  getChoiceFromParameters?: (parameters: Record<string, unknown>) => {
    choice: "optionA" | "optionB" | "optionC";
    filterId: string;
  };
  /** Question shown at the top of the configure step. */
  configureQuestion?: string;
  /** Column names for the automatic task binding. */
  fields: FieldNames;
  /**
   * Column names for one specific sync, when they cannot be fixed for the
   * whole integration. Falls back to `fields` when absent or when it returns
   * undefined.
   *
   * Jira is the motivating case: a project-scoped sync binds completion to the
   * issue's `statusId` so that Done and Discarded can write *different* Jira
   * statuses, while the cross-project scopes ("my issues", a saved filter) have
   * no enumerable status set and must keep the boolean `completed` column.
   */
  fieldsFor?: (parameters: Record<string, unknown>) => FieldNames | undefined;
  /** Optional provider-schema mapping step shown after filter selection. */
  taskMapping?: {
    /**
     * Whether the mapping step applies to the chosen scope. Absent means
     * always. Jira only offers it for project-scoped syncs, because status ids
     * belong to a project's workflow and the cross-project scopes span many.
     */
    appliesTo?: (
      choice: "optionA" | "optionB" | "optionC",
      filterId: string
    ) => boolean;
    fetchProperties: (
      connId: string,
      filterId: string,
      membrane: MembraneClient
    ) => Promise<TaskSyncMappingProperty[]>;
    defaultMapping: (properties: TaskSyncMappingProperty[]) => TaskSyncMapping;
  };
  /** Optional task rows shown in the success illustration (for TaskListIllustration). */
  illustrationRows?: IllustrationRow[];
  /** Vue component rendered as the success illustration + caption. */
  IllustrationComponent: Component;
  /**
   * Optional list of actions to present when a task is completed. The first
   * entry is the default and used when setting up the binding. If absent, no
   * selector is shown.
   */
  completionActions?: Array<{
    label: string;
    fields: FieldNames;
    /**
     * Optional predicate evaluated against the stored data-collection parameters.
     * When present, the action is only offered if it returns true. Useful for
     * hiding actions that only make sense for a specific filter (e.g. "Unstar"
     * only applies when the sync was set up for starred emails).
     */
    visibleFor?: (parameters: Record<string, unknown>) => boolean;
  }>;
};

export type MainPhase = "empty" | "ready";
export type WizardPhase =
  | "connecting"
  | "configure"
  | "mapping"
  | "creating"
  | "failed"
  | "success";
export type TaskSyncStatus = "pending" | "ready" | "error";

/** One configured task sync (one enabled data collection) of a connection. */
export type TaskSyncEntry = {
  /** Stable v-for key derived from connection + collection + parameters. */
  key: string;
  workspaceId: string;
  dataCollectionId: IntegrationAppEnabledDataCollectionId;
  dataCollection: IntegrationAppEnabledDataCollection;
  status: TaskSyncStatus;
  /** Routine table backing the sync, once it exists. */
  tableId: string | null;
  /** Short display name, e.g. "All tasks" or "Design tasks". */
  name: string;
  /** Ready-state hint sentence. */
  hint: string;
};

function dataCollectionParameters(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

/**
 * Extracts a plain string value from a Membrane Cell parameter.
 * Exported so config getHint implementations can use it without importing Cell.
 */
export function getStringCellParam(
  params: Record<string, unknown>,
  key: string
): string | undefined {
  const cell = params[key];
  return optionalStringProperty(cell, "value") ?? undefined;
}

// ── Composable ─────────────────────────────────────────────────────────────

export function useIntegrationSyncWorkflow(
  config: IntegrationSyncConfig,
  setup: MembraneSetup,
  controller: Controller,
  workspaceDatabases: Ref<GraphDatabaseDictionaryDictionary | undefined>,
  router: Router,
  route: RouteLocationNormalizedLoaded,
  targetWorkspaceId: Ref<string | undefined>
) {
  const {
    connections,
    allWorkspaceData,
    membrane,
    enabledDataCollections,
    createConnection,
    notifyConnectError,
    fetchConnectionOptions,
    addAccount: connectAccount,
    deleteConnection: baseDeleteConnection,
    deleteSource,
    forceRefreshSource,
    getSourceKey,
    getDataCollectionStatus,
    getDatabaseId,
  } = setup;

  // ── Main phase + wizard ─────────────────────────────────────────────────

  // Whether any account is connected (drives the empty vs. ready screen)
  const mainPhase = ref<MainPhase>("empty");

  // Wizard state: null means the wizard is closed
  const wizardOpen = ref(false);
  const wizardPhase = ref<WizardPhase | null>(null);
  // Which connection the wizard is currently configuring (null during OAuth)
  const wizardConnId = ref<string | null>(null);
  // Why the "creating" step stopped making progress, when it did.
  const wizardFailure = ref<DataCollectionFailure | null>(null);

  const wizardTitle = computed(() => {
    if (wizardPhase.value === "connecting") return `Connect ${config.label}`;
    if (wizardPhase.value === "mapping") return "Map task fields";
    if (wizardPhase.value === "creating") return `Setting up...`;
    if (wizardPhase.value === "failed") {
      return wizardFailure.value?.kind === "timeout"
        ? `Still setting up`
        : `${config.label} sync setup failed`;
    }
    if (wizardPhase.value === "success") return `${config.label} sync is ready`;
    return `Set up task sync`;
  });

  /**
   * User-facing explanation of a failed or stalled setup.
   *
   * A timeout is deliberately not phrased as a failure: the refresh job is
   * queued or slow, it has not given up, and the data source appears on the
   * ready screen as soon as it lands.
   */
  const wizardFailureMessage = computed(() => {
    const failure = wizardFailure.value;
    if (!failure) return "";
    if (failure.kind === "timeout") {
      return (
        `Setting up your ${config.label} sync is taking longer than usual. ` +
        `It is still running in the background — you can close this and the ` +
        `data source will appear once it is ready.`
      );
    }
    return (
      `We couldn't finish setting up your ${config.label} sync: ` +
      `${failure.description}`
    );
  });

  // ── Filter picker ───────────────────────────────────────────────────────

  const choice = ref<"optionA" | "optionB" | "optionC">("optionA");
  const filterId = ref("");
  const filterItems = ref<Record<string, FilterItem[]>>({});
  const filterItemsA = ref<Record<string, FilterItem[]>>({});
  const filterItemsC = ref<Record<string, FilterItem[]>>({});
  const filterItemsLoading = ref(false);
  const filterItemsError = ref("");
  const mappingProperties = ref<TaskSyncMappingProperty[]>([]);
  const taskSyncMapping = ref<TaskSyncMapping | null>(null);
  const mappingLoading = ref(false);

  // The connection currently targeted by the wizard's picker
  const activeConnId = computed(
    () => wizardConnId.value ?? connections.value[0]?.id ?? ""
  );

  const filterItemOptions = computed<SelectOption[]>(() => {
    const connId = activeConnId.value;
    const items =
      choice.value === "optionA"
        ? (filterItemsA.value[connId] ?? [])
        : choice.value === "optionC"
          ? (filterItemsC.value[connId] ?? [])
          : (filterItems.value[connId] ?? []);
    return items.map((p) => ({ value: p.id, label: p.name, aliases: [] }));
  });

  const filterSelectionValid = computed(() => {
    // A required-filter collection can only be enabled once a specific item is
    // picked — there is no valid empty-parameters ("all") payload.
    if (config.requiresFilter) {
      return filterId.value.length > 0;
    }
    if (choice.value === "optionA") {
      return !config.fetchFilterItemsA || filterId.value.length > 0;
    }
    return filterId.value.length > 0;
  });

  /**
   * Whether the currently configured filter matches a task sync that already
   * exists for the wizard's connection. Submitting it again would target the
   * same enabled-data-collection id, so it is blocked instead.
   */
  const duplicateSync = computed(
    () =>
      filterSelectionValid.value &&
      enabledDataCollections.value.some(
        (e) =>
          e.dataCollectionId.connection_id === activeConnId.value &&
          e.dataCollectionId.data_collection_key === config.collectionKey &&
          (config.taskMapping && config.getChoiceFromParameters
            ? config.getChoiceFromParameters(
                dataCollectionParameters(e.dataCollectionId.parameters)
              ).filterId === filterId.value
            : sameDataCollectionParameters(
                config.getParameters(choice.value, filterId.value),
                e.dataCollectionId.parameters
              ))
      )
  );

  const canSubmit = computed(
    () => filterSelectionValid.value && !duplicateSync.value
  );
  const canSubmitMapping = computed(
    () =>
      taskSyncMapping.value !== null &&
      (!taskSyncMapping.value.statusPropertyId ||
        taskSyncMapping.value.statusCells !== undefined)
  );

  const successFilterName = computed(() => {
    const connId = activeConnId.value;
    const allItems = [
      ...(filterItemsA.value[connId] ?? []),
      ...(filterItems.value[connId] ?? []),
      ...(filterItemsC.value[connId] ?? []),
    ];
    return (
      allItems.find((p) => p.id === filterId.value)?.name ?? filterId.value
    );
  });

  // ── Per-sync completion action ──────────────────────────────────────────

  // Entries of the task-sync collection that are ready (i.e. have a table and
  // an automatic task binding). Keyed by table id everywhere below since each
  // sync owns exactly one table / binding.
  const readySyncCollections = computed(() =>
    enabledDataCollections.value.flatMap((e) => {
      const dc = e.dataCollection;
      if (
        e.dataCollectionId.data_collection_key !== config.collectionKey ||
        dc.status !== "ready"
      ) {
        return [];
      }
      return [{ entry: e, tableId: dc.table }];
    })
  );

  // Stores the index into config.completionActions for each sync's table.
  const completionActionByTable = ref<Record<string, number>>({});

  /**
   * Per-sync completion action options, keyed by the sync's table id. Actions
   * with a `visibleFor` predicate are filtered against the stored
   * data-collection parameters of that sync, so e.g. "Unstar" only appears for
   * starred-email syncs.
   */
  const completionActionOptionsByTable = computed(
    (): Record<string, SelectOption[]> => {
      const result: Record<string, SelectOption[]> = {};
      for (const { entry, tableId } of readySyncCollections.value) {
        const params = dataCollectionParameters(
          entry.dataCollectionId.parameters
        );
        result[tableId] = (config.completionActions ?? [])
          .map((action, idx) => ({ action, idx }))
          .filter(
            ({ action }) => !action.visibleFor || action.visibleFor(params)
          )
          .map(({ action, idx }) => ({
            value: String(idx),
            label: action.label,
            aliases: Array<string>(),
          }));
      }
      return result;
    }
  );

  /**
   * Extracts the comparable payload of a status cell. The controller may
   * return cells in bare form (e.g. `false`) or union-key form
   * (`{ type: "bool", value: false }`), and JSON key order is not stable, so
   * comparing serialized whole cells is unreliable.
   */
  const cellPayload = (cell: Cell): unknown =>
    typeof cell === "object" && cell !== null && "value" in cell
      ? cell.value
      : cell;

  const sameCellWrite = (a: Cell, b: Cell): boolean =>
    JSON.stringify(cellPayload(a)) === JSON.stringify(cellPayload(b));

  // Guards against duplicate concurrent init calls per table.
  const completionActionInitPending = new Set<string>();

  const initCompletionActionForTable = async (
    tableId: string,
    workspaceId: string
  ) => {
    if (
      tableId in completionActionByTable.value ||
      completionActionInitPending.has(tableId)
    ) {
      return;
    }

    if (config.completionActions && config.completionActions.length > 0) {
      const dbFields =
        workspaceDatabases.value?.[workspaceId]?.[tableId]?.type?.fields;

      if (dbFields) {
        completionActionInitPending.add(tableId);
        try {
          const bindingsRef = await controller.task.binding.all();
          const bindings: TaskBindingDictionary = bindingsRef.release() ?? {};
          const binding = Object.values(bindings).find(
            (binding) =>
              binding.workspace_id === workspaceId &&
              binding.table_id === tableId
          );

          if (
            binding?.status_mapping &&
            binding.status_mapping.kind === "values"
          ) {
            const columnId = binding.status_mapping.column;
            const columnName = dbFields[columnId]?.name?.toLowerCase();
            const activeDoneWrite = binding.status_mapping.done.write;
            const defaultDoneWrite: Cell = { type: "bool", value: true };

            const actionIndex = config.completionActions.findIndex((a) => {
              if (a.fields.completed?.toLowerCase() !== columnName)
                return false;
              // A resolved mapping's cells are only known at bind time, and can
              // legitimately change when the external schema does (a renamed
              // Notion status option keeps its ID, a recreated one does not), so
              // the column match is all there is to compare on.
              if (a.fields.resolveStatusCells) return true;
              const expectedWrite =
                a.fields.customStatusCells?.done.write ?? defaultDoneWrite;
              return sameCellWrite(expectedWrite, activeDoneWrite);
            });
            // Re-check: the user may have picked an action while we awaited.
            if (
              actionIndex >= 0 &&
              !(tableId in completionActionByTable.value)
            ) {
              completionActionByTable.value = {
                ...completionActionByTable.value,
                [tableId]: actionIndex,
              };
            }
          }
        } finally {
          completionActionInitPending.delete(tableId);
        }
      }
    }
  };

  // ── Status mapping repair ───────────────────────────────────────────────

  /**
   * Tables already re-attempted this session, so the watcher below does not
   * resolve again on every `workspaceDatabases` tick.
   */
  const statusMappingRepairAttempted = new Set<string>();

  /**
   * Re-attempts the status mapping of a ready sync that ended up without one.
   *
   * An integration that sets `requireResolvedStatusCells` binds with
   * `status_mapping` unset when resolution fails, and nothing retries: the sync
   * then reports healthy forever while completing a task silently cannot write
   * back to the provider. The binding stays repairable after the fact, so try
   * again once per table whenever the integration screen is opened. A mapping
   * missed during setup (the provider rate limiting the wizard, the app offline
   * at the wrong moment) heals the next time the user looks at the screen, and
   * no repair state has to be persisted anywhere.
   *
   * Follows `useProvisionedRepair`: owns no resources, fired and forgotten, and
   * fails open — a repair that cannot run leaves the sync exactly as it was.
   */
  const repairStatusMappingForTable = async (
    entry: EnabledDataCollectionWithWorkspace,
    tableId: string
  ) => {
    // Only these can legitimately hold a binding with no status mapping; the
    // others fell back to boolean cells and have nothing to repair.
    //
    // Repairs with `config.fields` rather than a selected completion action's:
    // an action is only ever recorded for a table that already has a mapping
    // (see initCompletionActionForTable), so a table reaching here has none
    // selected. Today both opted-out integrations declare the same `completed`
    // column and resolver in each action anyway.
    if (
      !config.fields.resolveStatusCells ||
      !config.fields.requireResolvedStatusCells
    ) {
      return;
    }
    if (statusMappingRepairAttempted.has(tableId)) return;

    const completedName = config.fields.completed;
    if (!completedName) return;

    // Not yet loaded: this watcher fires again when the fields arrive.
    const dbFields =
      workspaceDatabases.value?.[entry.workspaceId]?.[tableId]?.type?.fields;
    if (!dbFields) return;

    const completedColumnId = Object.entries(dbFields).find(
      ([, f]) => f.name.toLowerCase() === completedName.toLowerCase()
    )?.[0];
    if (!completedColumnId) return;

    // Claimed before the first await: the watcher can fire again while the
    // bindings read and the resolution are still in flight.
    statusMappingRepairAttempted.add(tableId);

    try {
      const bindingsRef = await controller.task.binding.all();
      const bindings: TaskBindingDictionary = bindingsRef.release() ?? {};
      const bindingEntry = Object.entries(bindings).find(
        ([, b]) => b.table_id === tableId
      );
      if (!bindingEntry) return;

      const [bindingId, binding] = bindingEntry;
      // Already mapped, by setup or by the user: leave it alone.
      if (binding.status_mapping) return;

      const statusCells = await resolveStatusCellMapping(
        config.fields,
        entry.dataCollectionId.connection_id,
        dataCollectionParameters(entry.dataCollectionId.parameters),
        membrane.value
      );
      // Still unresolved. resolveStatusCellMapping has logged why, and the
      // binding keeps its honest unmapped state.
      if (!statusCells) return;

      const { rows: _rows, ...existingConfig } = binding;
      await controller.task.binding.upsert(bindingId, {
        ...existingConfig,
        status_mapping: {
          kind: "values" as const,
          column: completedColumnId,
          todo: statusCells.todo,
          done: statusCells.done,
          discarded: statusCells.discarded,
        },
      });
      logger.info("Repaired a task binding with no status mapping", {
        integration: config.integrationKey,
        collection: config.collectionKey,
        connectionId: entry.dataCollectionId.connection_id,
        tableId,
      });
    } catch (error) {
      logger.warn("Could not repair a task binding status mapping", {
        integration: config.integrationKey,
        tableId,
        error,
      });
    }
  };

  const setCompletionAction = async (
    sync: TaskSyncEntry,
    actionIndex: number
  ) => {
    const tableId = sync.tableId;
    if (tableId) {
      completionActionByTable.value = {
        ...completionActionByTable.value,
        [tableId]: actionIndex,
      };
    }

    const action = config.completionActions?.[actionIndex];

    if (action && tableId) {
      const dbFields =
        workspaceDatabases.value?.[sync.workspaceId]?.[tableId]?.type?.fields;

      if (dbFields) {
        const bindingsRef = await controller.task.binding.all();
        const bindings: TaskBindingDictionary = bindingsRef.release() ?? {};
        const bindingEntry = Object.entries(bindings).find(
          ([, binding]) =>
            binding.workspace_id === sync.workspaceId &&
            binding.table_id === tableId
        );

        if (bindingEntry) {
          const [bindingId, binding] = bindingEntry;

          const findColumnId = (name: string): string | undefined =>
            Object.entries(dbFields).find(
              ([, f]) => f.name.toLowerCase() === name.toLowerCase()
            )?.[0];

          const completedColumnId = action.fields.completed
            ? findColumnId(action.fields.completed)
            : undefined;

          if (completedColumnId) {
            const statusCells = await resolveStatusCellMapping(
              action.fields,
              sync.dataCollectionId.connection_id,
              dataCollectionParameters(sync.dataCollectionId.parameters),
              membrane.value
            );

            // An unresolved mapping (already reported by
            // resolveStatusCellMapping) must not clobber the binding's working
            // status mapping with cells that cannot match the column: keep the
            // previously selected completion action instead.
            if (statusCells) {
              const { rows: _rows, ...existingConfig } = binding;

              await controller.task.binding.upsert(bindingId, {
                ...existingConfig,
                status_mapping: {
                  kind: "values" as const,
                  column: completedColumnId,
                  todo: statusCells.todo,
                  done: statusCells.done,
                  discarded: statusCells.discarded,
                },
              });
            }
          }
        }
      }
    }
  };

  // ── Per-connection display labels ───────────────────────────────────────

  const connectionLabels = ref<Record<string, string>>({});

  const fetchAndCacheConnectionLabel = async (connId: string) => {
    if (!config.fetchConnectionLabel || connectionLabels.value[connId]) return;
    try {
      const label = await config.fetchConnectionLabel(connId, membrane.value);
      if (label) {
        connectionLabels.value = { ...connectionLabels.value, [connId]: label };
      }
    } catch {
      // Label stays unknown; fall back to getConnectionDetail in the template.
    }
  };

  // ── Per-connection task sync entries ────────────────────────────────────

  /**
   * All task syncs of each connection, in display form: one entry per enabled
   * data collection of the task-sync collection key, with its resolved name,
   * hint and status.
   */
  const taskSyncsByConnection = computed(
    (): Record<string, TaskSyncEntry[]> => {
      const result: Record<string, TaskSyncEntry[]> = {};
      for (const conn of connections.value) {
        const entries = enabledDataCollections.value.filter(
          (e) =>
            e.dataCollectionId.connection_id === conn.id &&
            e.dataCollectionId.data_collection_key === config.collectionKey
        );
        const allItems = [
          ...(filterItemsA.value[conn.id] ?? []),
          ...(filterItems.value[conn.id] ?? []),
          ...(filterItemsC.value[conn.id] ?? []),
        ];
        result[conn.id] = entries.map((e) => {
          const parameters = dataCollectionParameters(
            e.dataCollectionId.parameters
          );
          return {
            key: getSourceKey(e.dataCollectionId),
            workspaceId: e.workspaceId,
            dataCollectionId: e.dataCollectionId,
            dataCollection: e.dataCollection,
            status: getDataCollectionStatus(e.dataCollection),
            tableId: getDatabaseId(e.dataCollection),
            name: config.getSyncName(parameters, allItems),
            hint: config.getHint(parameters, allItems, null),
          };
        });
      }
      return result;
    }
  );

  // ── Filter item fetching ────────────────────────────────────────────────

  // Silent variants for background/eager loading — no loading spinner.
  const fetchFilterItemsForConnSilent = async (connId: string) => {
    try {
      const fetched = await config.fetchFilterItems(connId, membrane.value);
      filterItems.value = { ...filterItems.value, [connId]: fetched };
    } catch {
      // Non-critical background fetch; ignore errors.
    }
  };

  const fetchFilterItemsAForConnSilent = async (connId: string) => {
    if (!config.fetchFilterItemsA) return;
    try {
      const fetched = await config.fetchFilterItemsA(connId, membrane.value);
      filterItemsA.value = { ...filterItemsA.value, [connId]: fetched };
    } catch {
      // Non-critical background fetch; ignore errors.
    }
  };

  const fetchFilterItemsCForConnSilent = async (connId: string) => {
    if (!config.fetchFilterItemsC) return;
    try {
      const fetched = await config.fetchFilterItemsC(connId, membrane.value);
      filterItemsC.value = { ...filterItemsC.value, [connId]: fetched };
    } catch {
      // Non-critical background fetch; ignore errors.
    }
  };

  /**
   * Reports a failed picker fetch inline instead of letting it escape.
   *
   * These fetches hit the external API through Membrane, so they fail for
   * reasons the user can act on (an account that lost access, a plan that does
   * not expose the collection). The callers below are fire-and-forget, so an
   * uncaught rejection would surface as the app's fatal error screen.
   */
  const reportFilterItemsFailure = (connId: string, error: unknown) => {
    logger.error("Failed to fetch filter items", {
      integration: config.integrationKey,
      collection: config.collectionKey,
      connId,
      error,
    });
    filterItemsError.value =
      `We couldn't load your ${config.label} options. The connected account ` +
      `may not have access to them.`;
  };

  // User-triggered variants — show the loading spinner inside the picker.
  const fetchFilterItemsForConn = async (connId: string) => {
    filterItemsLoading.value = true;
    filterItemsError.value = "";
    try {
      const fetched = await config.fetchFilterItems(connId, membrane.value);
      filterItems.value = { ...filterItems.value, [connId]: fetched };
    } catch (error) {
      reportFilterItemsFailure(connId, error);
    } finally {
      filterItemsLoading.value = false;
    }
  };

  const fetchFilterItemsAForConn = async (connId: string) => {
    if (!config.fetchFilterItemsA) return;
    filterItemsLoading.value = true;
    filterItemsError.value = "";
    try {
      const fetched = await config.fetchFilterItemsA(connId, membrane.value);
      filterItemsA.value = { ...filterItemsA.value, [connId]: fetched };
    } catch (error) {
      reportFilterItemsFailure(connId, error);
    } finally {
      filterItemsLoading.value = false;
    }
  };

  const fetchFilterItemsCForConn = async (connId: string) => {
    if (!config.fetchFilterItemsC) return;
    filterItemsLoading.value = true;
    filterItemsError.value = "";
    try {
      const fetched = await config.fetchFilterItemsC(connId, membrane.value);
      filterItemsC.value = { ...filterItemsC.value, [connId]: fetched };
    } catch (error) {
      reportFilterItemsFailure(connId, error);
    } finally {
      filterItemsLoading.value = false;
    }
  };

  // ── Connections watcher — placed after fetch functions to avoid TDZ ──────

  const stopWatchConnections = watch(
    connections,
    (conns) => {
      mainPhase.value = conns.length > 0 ? "ready" : "empty";
      for (const conn of conns) {
        void fetchAndCacheConnectionLabel(conn.id);
        // Eagerly load both item sets so ready-state hints can always resolve
        // a live name without having to parse the stored database name.
        if ((filterItems.value[conn.id] ?? []).length === 0) {
          void fetchFilterItemsForConnSilent(conn.id);
        }
        if (
          config.fetchFilterItemsA &&
          (filterItemsA.value[conn.id] ?? []).length === 0
        ) {
          void fetchFilterItemsAForConnSilent(conn.id);
        }
        if (
          config.fetchFilterItemsC &&
          (filterItemsC.value[conn.id] ?? []).length === 0
        ) {
          void fetchFilterItemsCForConnSilent(conn.id);
        }
      }
    },
    { immediate: true }
  );

  // Initialize completion action state for ready syncs, and repair any binding
  // left without a status mapping. Also watches workspaceDatabases because on
  // page load a sync can be "ready" before the database fields have arrived, in
  // which case the first attempt bails and must be retried once the fields are
  // available.
  const stopWatchCompletionActions = watch(
    [readySyncCollections, workspaceDatabases],
    () => {
      for (const { entry, tableId } of readySyncCollections.value) {
        if (
          config.completionActions &&
          !(tableId in completionActionByTable.value)
        ) {
          void initCompletionActionForTable(tableId, entry.workspaceId);
        }
        void repairStatusMappingForTable(entry, tableId);
      }
    },
    { immediate: true }
  );

  const onFilterOptionAClick = () => {
    choice.value = "optionA";
    filterId.value = "";
    const connId = activeConnId.value;
    if (
      config.fetchFilterItemsA &&
      connId &&
      (filterItemsA.value[connId] ?? []).length === 0 &&
      !filterItemsLoading.value
    ) {
      void fetchFilterItemsAForConn(connId);
    }
  };

  const onFilterOptionBClick = () => {
    choice.value = "optionB";
    filterId.value = "";
    const connId = activeConnId.value;
    if (
      connId &&
      (filterItems.value[connId] ?? []).length === 0 &&
      !filterItemsLoading.value
    ) {
      void fetchFilterItemsForConn(connId);
    }
  };

  const onFilterOptionCClick = () => {
    choice.value = "optionC";
    filterId.value = "";
    const connId = activeConnId.value;
    if (
      config.fetchFilterItemsC &&
      connId &&
      (filterItemsC.value[connId] ?? []).length === 0 &&
      !filterItemsLoading.value
    ) {
      void fetchFilterItemsCForConn(connId);
    }
  };

  // ── Watching for data collection ready ─────────────────────────────────

  // Guards against setting up two watchers for the same sync. Keys combine the
  // workspace and connection ids with a serialization of the sync's parameters.
  const watchedSyncCleanups = new Map<string, () => void>();
  const repairingTaskBindings = ref<Set<string>>(new Set());
  let released = false;

  const watchForReady = (
    workspaceId: string,
    connId: string,
    watchKey: string,
    matchesParameters: (parameters: unknown) => boolean,
    parameters: Record<string, unknown>
  ) => {
    const key = `${workspaceId}:${connId}:${watchKey}`;
    if (released || watchedSyncCleanups.has(key)) return;
    const workspace = allWorkspaceData.find(
      (item) => item.workspaceId === workspaceId
    );
    if (!workspace) return;

    let cleanup = () => {};
    const release = () => {
      cleanup();
      watchedSyncCleanups.delete(key);
    };

    cleanup = watchForDataCollectionReady(
      workspace,
      controller,
      workspaceDatabases,
      connId,
      config.collectionKey,
      matchesParameters,
      config.fieldsFor?.(parameters) ?? config.fields,
      () => membrane.value,
      () => {
        release();
        // Show success in the wizard only if it is currently open for this connection.
        if (wizardConnId.value === connId && wizardOpen.value) {
          wizardFailure.value = null;
          wizardPhase.value = "success";
        }
      },
      (failure) => {
        // An API collection error can recover on its own, so its watcher
        // stays active. Binding creation has already stopped its watcher;
        // release its dedicated subscription and let the user repair it.
        logger.warn("Membrane task sync setup did not complete", {
          integration: config.integrationKey,
          collection: config.collectionKey,
          connectionId: connId,
          failure: failure.kind,
          ...(failure.kind !== "timeout"
            ? { description: failure.description }
            : {}),
        });

        const wizardIsWaiting =
          wizardConnId.value === connId &&
          wizardOpen.value &&
          wizardPhase.value === "creating";
        if (wizardIsWaiting) {
          wizardFailure.value = failure;
          wizardPhase.value = "failed";
        } else if (failure.kind !== "timeout") {
          controller.notifications.notify(
            `${config.label} sync setup failed: ${failure.description}`
          );
        }

        if (failure.kind === "binding") release();
      }
    );
    watchedSyncCleanups.set(key, release);
  };

  // Resume watching for any pending data collections so that the creating
  // state survives page refreshes without re-opening the wizard.
  for (const workspace of allWorkspaceData) {
    for (const [id, dc] of workspace.membraneData.value
      ?.enabled_data_collections ?? []) {
      const belongsToSync =
        connections.value.some((c) => c.id === id.connection_id) &&
        id.data_collection_key === config.collectionKey;
      if (belongsToSync && dc.status === "creation_pending") {
        // Both sides of the comparison come from the same stored data, so plain
        // JSON equality of the parameters identifies this exact sync.
        const storedParams = JSON.stringify(id.parameters);
        watchForReady(
          workspace.workspaceId,
          id.connection_id,
          storedParams,
          (parameters) => JSON.stringify(parameters) === storedParams,
          dataCollectionParameters(id.parameters)
        );
      }
    }
  }

  const release = () => {
    if (released) return;
    released = true;
    stopWatchConnections();
    stopWatchCompletionActions();
    for (const cleanup of [...watchedSyncCleanups.values()]) cleanup();
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const continueConfig = async () => {
    const mapping = config.taskMapping;
    if (
      !mapping ||
      (mapping.appliesTo && !mapping.appliesTo(choice.value, filterId.value))
    ) {
      await submitConfig();
      return;
    }
    const connId = activeConnId.value;
    if (!connId || (config.requiresFilter && !filterId.value)) return;
    mappingLoading.value = true;
    taskSyncMapping.value = {
      statusPropertyId: "",
      datePropertyId: "",
    };
    wizardPhase.value = "mapping";
    try {
      const properties = await mapping.fetchProperties(
        connId,
        filterId.value,
        membrane.value
      );
      mappingProperties.value = properties;
      taskSyncMapping.value = mapping.defaultMapping(properties);
    } finally {
      mappingLoading.value = false;
    }
  };

  const submitConfig = async () => {
    const connId = wizardConnId.value;
    const conn = connId
      ? connections.value.find((c) => c.id === connId)
      : connections.value[0];
    const workspace = allWorkspaceData.find(
      ({ workspaceId }) => workspaceId === targetWorkspaceId.value
    );
    if (!conn || !workspace) {
      controller.notifications.notify(
        `Could not find the default workspace for this ${config.label} sync.`
      );
      return;
    }

    const allItems = [
      ...(filterItemsA.value[conn.id] ?? []),
      ...(filterItems.value[conn.id] ?? []),
      ...(filterItemsC.value[conn.id] ?? []),
    ];
    const filterName =
      choice.value === "optionA" && !config.fetchFilterItemsA
        ? ""
        : (allItems.find((p) => p.id === filterId.value)?.name ??
          filterId.value);

    wizardFailure.value = null;
    wizardPhase.value = "creating";

    const parameters = config.getParameters(
      choice.value,
      filterId.value,
      taskSyncMapping.value ?? undefined
    );

    await controller.integrationApp.enableDataCollection(
      workspace.workspaceId,
      {
        connection_id: conn.id,
        data_collection_key: config.collectionKey,
        parameters,
      },
      {
        status: "creation_pending",
        database_display_name: config.getDisplayName(
          choice.value,
          filterName,
          connectionLabels.value[conn.id]
        ),
        integration_key: config.integrationKey,
        pin_to_personal_workspace_menu: false,
      }
    );

    watchForReady(
      workspace.workspaceId,
      conn.id,
      JSON.stringify(parameters),
      (stored) => sameDataCollectionParameters(parameters, stored),
      parameters
    );
  };

  const repairTaskBinding = async (sync: TaskSyncEntry, reset = false) => {
    const workspace = allWorkspaceData.find(
      ({ workspaceId }) => workspaceId === sync.workspaceId
    );
    if (!workspace || sync.dataCollection.status !== "ready") return;

    const repairKey = `${sync.workspaceId}:${sync.dataCollection.table}`;
    if (repairingTaskBindings.value.has(repairKey)) return;
    repairingTaskBindings.value = new Set([
      ...repairingTaskBindings.value,
      repairKey,
    ]);

    try {
      await ensureDataCollectionBinding(
        workspace,
        controller,
        workspaceDatabases,
        sync.dataCollectionId,
        sync.dataCollection,
        config.fieldsFor?.(
          dataCollectionParameters(sync.dataCollectionId.parameters)
        ) ?? config.fields,
        () => membrane.value,
        reset
      );
    } catch (error) {
      logger.error("Failed to repair integration task binding", {
        integration: config.integrationKey,
        connectionId: sync.dataCollectionId.connection_id,
        tableId: sync.tableId,
        error,
      });
      controller.notifications.notify(
        `Could not set up this ${config.label} task binding. Try again.`
      );
    } finally {
      const next = new Set(repairingTaskBindings.value);
      next.delete(repairKey);
      repairingTaskBindings.value = next;
    }
  };

  // ── Wizard lifecycle ────────────────────────────────────────────────────

  /** Show the success illustration modal for a task sync that is already active. */
  const showHowToUse = (connId: string, sync: TaskSyncEntry) => {
    // Restore choice + filterId from the sync's data collection so the
    // illustration reflects the actual configured filter, not the defaults.
    if (config.getChoiceFromParameters) {
      const restored = config.getChoiceFromParameters(
        dataCollectionParameters(sync.dataCollectionId.parameters)
      );
      choice.value = restored.choice;
      filterId.value = restored.filterId;
    }
    wizardConnId.value = connId;
    wizardPhase.value = "success";
    wizardOpen.value = true;
  };

  /** Open the task sync setup wizard for a specific connected account. */
  const openTaskSyncWizard = (connId: string) => {
    wizardConnId.value = connId;
    // Required-filter collections have no "all" default: start on the picker
    // (optionB) and make sure its items are loaded so a list can be selected.
    choice.value = config.requiresFilter ? "optionB" : "optionA";
    filterId.value = "";
    mappingProperties.value = [];
    taskSyncMapping.value = null;
    mappingLoading.value = false;
    if (
      config.requiresFilter &&
      (filterItems.value[connId] ?? []).length === 0 &&
      !filterItemsLoading.value
    ) {
      void fetchFilterItemsForConn(connId);
    }
    wizardFailure.value = null;
    wizardPhase.value = "configure";
    wizardOpen.value = true;
  };

  const closeSuccess = () => {
    wizardOpen.value = false;
    // onWizardClosed handles the rest of the cleanup.
  };

  /**
   * Send a failed setup back to the picker so the user can pick a different
   * filter (or the same one again).
   *
   * Only offered for an outright error: a timeout means the original attempt is
   * still in flight, and re-submitting the same parameters would target the
   * same enabled-data-collection id.
   */
  const retryFailedSetup = () => {
    wizardFailure.value = null;
    wizardPhase.value = "configure";
  };

  const onWizardClosed = () => {
    wizardPhase.value = null;
    wizardConnId.value = null;
    wizardFailure.value = null;
    choice.value = "optionA";
    filterId.value = "";
  };

  // ── Connection lifecycle ────────────────────────────────────────────────

  /**
   * Opens the OAuth popup to connect a new account. After the OAuth completes
   * (or is cancelled), the wizard closes and the ready screen is shown — no
   * configure step is forced. Task sync can be set up independently via
   * openTaskSyncWizard.
   */
  const addAccount = async () => {
    wizardConnId.value = null;
    const options = await fetchConnectionOptions();
    const option = options.length === 1 ? options[0] : undefined;
    // An option with no input fields is an interactive (OAuth) method; anything
    // that requires input (API key, basic auth, …) or offers multiple methods
    // must go through the shared flow, which shows the connection-type picker
    // and/or an input modal to collect credentials before connecting.
    const isOAuth =
      !!option &&
      Object.keys(option.inputSchema?.properties ?? {}).length === 0;

    if (!isOAuth) {
      await connectAccount();
      return;
    }

    // Single OAuth method: connect directly and show the in-app
    // "Waiting for authorization…" spinner while the popup is open.
    wizardPhase.value = "connecting";
    wizardOpen.value = true;
    try {
      const newConn = await createConnection(option.key);
      if (newConn) void fetchAndCacheConnectionLabel(newConn.id);
    } catch (error) {
      // A refused third-party OAuth (e.g. Facebook returning 400) rejects
      // with a MembraneError; surface it instead of crashing the app.
      notifyConnectError(error);
    } finally {
      wizardOpen.value = false;
    }
  };

  /**
   * Removes a single task sync (its data collection and task binding) without
   * touching the connection or the account's other syncs.
   */
  const removeSync = async (sync: TaskSyncEntry) => {
    await deleteSource(
      sync.workspaceId,
      sync.dataCollectionId,
      sync.dataCollection
    );
  };

  /**
   * Requests an immediate re-synchronization of a single task sync. Mirrors
   * removeSync, but re-enables the collection with the `force_refresh` status
   * instead of disabling it.
   */
  const forceRefreshSync = async (sync: TaskSyncEntry) => {
    await forceRefreshSource(
      sync.workspaceId,
      sync.dataCollectionId,
      sync.dataCollection
    );
  };

  /**
   * Disconnects an account. Also removes all its task syncs first so no stale
   * bindings or data collections are left behind.
   */
  const deleteConnection = async (connectionId: string) => {
    const collectionsForConn = enabledDataCollections.value.filter(
      (e) =>
        e.dataCollectionId.connection_id === connectionId &&
        e.dataCollectionId.data_collection_key === config.collectionKey
    );
    await Promise.all(
      collectionsForConn.map((e) =>
        deleteSource(e.workspaceId, e.dataCollectionId, e.dataCollection)
      )
    );
    await baseDeleteConnection(connectionId);
  };

  // ── Auto-trigger from query param ──────────────────────────────────────

  if (route.query.addAccount === "true") {
    router.replace({ query: { ...route.query, addAccount: undefined } });
    addAccount();
  }

  return {
    mainPhase,
    wizardOpen,
    wizardPhase,
    wizardTitle,
    wizardConnId,
    wizardFailure,
    wizardFailureMessage,
    retryFailedSetup,
    repairTaskBinding,
    repairingTaskBindings,
    taskSyncsByConnection,
    choice,
    filterId,
    filterItemOptions,
    filterItemsLoading,
    filterItemsError,
    mappingProperties,
    taskSyncMapping,
    mappingLoading,
    canSubmit,
    canSubmitMapping,
    duplicateSync,
    successFilterName,
    connectionLabels,
    completionActionByTable,
    completionActionOptionsByTable,
    setCompletionAction,
    onFilterOptionAClick,
    onFilterOptionBClick,
    onFilterOptionCClick,
    showHowToUse,
    openTaskSyncWizard,
    continueConfig,
    submitConfig,
    addAccount,
    closeSuccess,
    onWizardClosed,
    removeSync,
    forceRefreshSync,
    deleteConnection,
    release,
  };
}

// ── No-task-sync variant ────────────────────────────────────────────────────

/**
 * Minimal workflow for integrations that have no task-sync config (e.g. GitHub,
 * Linear). Returns the same shape as useIntegrationSyncWorkflow so
 * IntegrationSyncWorkflow can use a single code path.
 *
 * No connection reports any task sync, so the per-account setup area is shown,
 * but only the data-sources UI is available (no wizard configure step).
 */
export function useNoTaskSyncWorkflow(
  setup: MembraneSetup,
  router: Router,
  route: RouteLocationNormalizedLoaded
): ReturnType<typeof useIntegrationSyncWorkflow> {
  const {
    connections,
    fetchConnectionOptions,
    createConnection,
    notifyConnectError,
    addAccount: connectAccount,
    deleteConnection: baseDeleteConnection,
  } = setup;

  const mainPhase = computed<MainPhase>(() =>
    connections.value.length > 0 ? "ready" : "empty"
  );

  const wizardOpen = ref(false);
  const wizardPhase = ref<WizardPhase | null>(null);

  const addAccount = async () => {
    const options = await fetchConnectionOptions();
    const option = options.length === 1 ? options[0] : undefined;
    const isOAuth =
      !!option &&
      Object.keys(option.inputSchema?.properties ?? {}).length === 0;

    if (!isOAuth) {
      await connectAccount();
      return;
    }

    wizardPhase.value = "connecting";
    wizardOpen.value = true;
    try {
      await createConnection(option.key);
    } catch (error) {
      // A refused third-party OAuth (e.g. Facebook returning 400) rejects
      // with a MembraneError; surface it instead of crashing the app.
      notifyConnectError(error);
    } finally {
      wizardOpen.value = false;
      wizardPhase.value = null;
    }
  };

  const deleteConnection = async (connId: string) => {
    await baseDeleteConnection(connId);
  };

  if (route.query.addAccount === "true") {
    router.replace({ query: { ...route.query, addAccount: undefined } });
    addAccount();
  }

  return {
    mainPhase,
    wizardOpen,
    wizardPhase,
    wizardTitle: computed(() =>
      wizardPhase.value === "connecting" ? "Connecting…" : ""
    ),
    wizardConnId: ref<string | null>(null),
    // No task sync to set up, so the wizard never reaches a failed state.
    wizardFailure: ref<DataCollectionFailure | null>(null),
    wizardFailureMessage: computed(() => ""),
    retryFailedSetup: () => {},
    repairTaskBinding: async (_sync: TaskSyncEntry) => {},
    repairingTaskBindings: ref<Set<string>>(new Set()),
    taskSyncsByConnection: computed<Record<string, TaskSyncEntry[]>>(
      () => ({})
    ),
    choice: ref<"optionA" | "optionB" | "optionC">("optionA"),
    filterId: ref(""),
    filterItemOptions: computed<SelectOption[]>(() => []),
    filterItemsLoading: ref(false),
    filterItemsError: ref(""),
    mappingProperties: ref<TaskSyncMappingProperty[]>([]),
    taskSyncMapping: ref<TaskSyncMapping | null>(null),
    mappingLoading: ref(false),
    canSubmit: computed(() => false),
    canSubmitMapping: computed(() => false),
    duplicateSync: computed(() => false),
    successFilterName: computed(() => ""),
    connectionLabels: ref<Record<string, string>>({}),
    completionActionByTable: ref<Record<string, number>>({}),
    completionActionOptionsByTable: computed<Record<string, SelectOption[]>>(
      () => ({})
    ),
    setCompletionAction: async () => {},
    onFilterOptionAClick: () => {},
    onFilterOptionBClick: () => {},
    onFilterOptionCClick: () => {},
    showHowToUse: () => {},
    openTaskSyncWizard: () => {},
    continueConfig: async () => {},
    submitConfig: async () => {},
    addAccount,
    closeSuccess: () => {
      wizardOpen.value = false;
    },
    onWizardClosed: () => {
      wizardPhase.value = null;
    },
    removeSync: async () => {},
    forceRefreshSync: async () => {},
    deleteConnection,
    release: () => {},
  };
}
